import React from "react";
import { useDrag } from "react-dnd";
import { Box, Tooltip, Typography } from "@mui/material";
import { resolveBoothIcon } from "../../utils/IconMap";
import { useLocalization } from "../../context/LocalizationContext";

const STATUS_DOT = {
  pending:  '#f59e0b',
  rejected: '#ef4444',
};

const DraggableIcon = ({ icon, isMobile = false, isSelected = false, onSelect, onDismiss = null }) => {
  const approvalStatus = icon?.approvalStatus ?? null;
  const isPending  = approvalStatus === 'pending';
  const isRejected = approvalStatus === 'rejected';
  const dotColor   = STATUS_DOT[approvalStatus] ?? null;

  // deps array required — without it canDrag closes over the initial isPending value
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "ICON",
      item: icon,
      canDrag: () => !isPending,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [icon, isPending],
  );

  const { locale, t } = useLocalization();
  const label =
    icon?.localizedPairs?.values?.find((v) => v.localeId === locale)?.value ||
    icon?.localizedPairs?.values?.[0]?.value ||
    t(icon?.localizedPairs?.key, '') ||
    '';

  const tooltipParts = [];
  if (isPending)  tooltipParts.push("Pending approval — drag locked");
  if (isRejected) tooltipParts.push("Rejected — dismiss to resubmit");
  const tooltipTitle = tooltipParts.join(" · ") || undefined;

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow disableHoverListener={!tooltipTitle}>
      <Box
        ref={isMobile || isPending ? null : drag}
        onClick={isMobile && !isPending ? () => onSelect(icon) : undefined}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          cursor: isPending ? "not-allowed" : isMobile ? "pointer" : "grab",
          opacity: isDragging || isPending ? 0.5 : 1,
          width: { xs: 64, sm: 72, md: 84 },
          position: "relative",
        }}
      >
        {/* Status dot — top-right corner */}
        {dotColor && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: dotColor,
              border: "2px solid #fff",
              zIndex: 2,
            }}
          />
        )}

        {/* Icon box */}
        <Box
          sx={{
            width: { xs: 52, sm: 60, md: 70 },
            height: { xs: 52, sm: 60, md: 70 },
            borderRadius: "8px",
            bgcolor: "#0a326d",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: isMobile && isSelected
              ? "0 0 0 3px #5383b3, 0 4px 12px rgba(10,50,109,0.4)"
              : isRejected
                ? "0 0 0 2px #ef4444"
                : isPending
                  ? "0 0 0 2px #f59e0b"
                  : "0 2px 6px rgba(0,0,0,0.15)",
            transform: isMobile && isSelected ? "scale(1.1)" : "scale(1)",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            flexShrink: 0,
          }}
        >
          <img
            src={resolveBoothIcon(icon, "btn_tt1")}
            alt={label}
            style={{ width: "60%", height: "60%", objectFit: "contain" }}
          />
        </Box>

        {/* Label */}
        <Typography
          variant="caption"
          sx={{
            color: isMobile && isSelected ? "#0a326d" : "#1e3a5f",
            fontWeight: isMobile && isSelected ? 800 : 600,
            textAlign: "center",
            lineHeight: 1.2,
            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.72rem" },
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            width: "100%",
          }}
        >
          {label}
        </Typography>

        {/* Dismiss link for rejected unplaced buttons */}
        {isRejected && onDismiss && (
          <Typography
            variant="caption"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(icon.id, icon.approvalId);
            }}
            sx={{
              color: "#ef4444",
              fontWeight: 700,
              fontSize: "0.6rem",
              cursor: "pointer",
              textDecoration: "underline",
              mt: -0.5,
            }}
          >
            Dismiss
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default DraggableIcon;
